import * as React from "react";
import { useMemo, useState } from "react";
import { vorgangService, type TodoDto } from "../../../services/vorgangService";
import { IcoX } from "../v3/vg3Icons";
import { teilenFehler, teilenFolgeText, teilenVorschlag } from "./vorgangTeilen";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";

/**
 * „Vorgang teilen…" (PO 28.09.2026: „aus einem Vorgang zwei Vorgänge machen").
 *
 * Gewählt werden To-dos — sie sind die Einheit, in der ein gegliederter
 * Vorgang seine Arbeit trägt. Was angehakt ist, wandert samt Schriftwechsel in
 * einen NEUEN Vorgang; der Rest bleibt hier. Die Regeln (mindestens eines
 * bleibt, Betreff nötig) stehen in `vorgangTeilen.ts`.
 *
 * Dieselbe Hülle wie `VorgangAlsTodoVerschiebenDialog` — kein Fluent-Dialog
 * (PO 09.09.2026).
 */
type Props = {
    vorgangId: number;
    /** Betreff dieses Vorgangs — für die Folge im Klartext. */
    titel: string;
    todos: TodoDto[];
    onClose: () => void;
    /** Geteilt — die Seite lädt neu; `neueId` ist der neue Vorgang. */
    onGeteilt: (neueId: number, titel: string) => void | Promise<void>;
};

export const VorgangTeilenDialog: React.FC<Props> = ({ vorgangId, titel, todos, onClose, onGeteilt }) => {
    const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
    /** `null` = unberührt: Das Feld folgt dann dem Vorschlag aus der Auswahl. */
    const [neuerTitel, setNeuerTitel] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const vorschlag = useMemo(
        () => teilenVorschlag(todos.filter((t) => gewaehlt.has(t.id)).map((t) => t.titel ?? "")),
        [todos, gewaehlt],
    );
    const betreff = neuerTitel ?? vorschlag;
    const sperre = teilenFehler({ gewaehlt: gewaehlt.size, gesamt: todos.length, titel: betreff });

    const umschalten = (id: number) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(id)) neu.delete(id);
            else neu.add(id);
            return neu;
        });

    const teilen = async () => {
        if (busy || sperre) return;
        setBusy(true);
        setFehler(null);
        try {
            const { vorgangId: neueId } = await vorgangService.teileVorgang(
                vorgangId,
                todos.filter((t) => gewaehlt.has(t.id)).map((t) => t.id),
                betreff.trim(),
            );
            await onGeteilt(neueId, betreff.trim());
            onClose();
        } catch (err: any) {
            console.error("[VorgangTeilen] Fehlgeschlagen", err);
            // Offen bleiben: Auswahl und Betreff wären sonst verloren.
            setFehler(err?.message ?? "Teilen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd vg3-modal-hg" role="presentation" onClick={() => !busy && onClose()}>
            <div
                className="vg3-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Vorgang teilen"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">Vorgang teilen</span>
                    <button
                        type="button"
                        className="vg3-ibtn"
                        title="Schließen"
                        aria-label="Schließen"
                        disabled={busy}
                        onClick={onClose}
                    >
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                <div className="vg3-modal-b">
                    <p className="vg3-modal-hinweis">
                        Welche To-dos sollen in einen neuen, eigenen Vorgang? Alles, was an ihnen hängt, wandert mit.
                    </p>
                    <div className="vgdup-liste" role="group" aria-label="To-dos für den neuen Vorgang">
                        {todos.map((t) => (
                            <label key={t.id} className="vgdup-zeile">
                                <input
                                    type="checkbox"
                                    checked={gewaehlt.has(t.id)}
                                    disabled={busy}
                                    onChange={() => umschalten(t.id)}
                                />
                                <span className="vgdup-art">To-do</span>
                                <span className="vgdup-text">{t.titel || `To-do ${t.id}`}</span>
                            </label>
                        ))}
                    </div>

                    <label className="vg3-zeile-label" htmlFor="vg4-teilen-titel" style={{ display: "block", marginTop: 12 }}>
                        Betreff des neuen Vorgangs
                    </label>
                    <input
                        id="vg4-teilen-titel"
                        className="vg4-umb-feld"
                        value={betreff}
                        placeholder="Betreff…"
                        disabled={busy}
                        onChange={(e) => setNeuerTitel(e.target.value)}
                    />

                    <p className="vg3-modal-hinweis">
                        {sperre ??
                            teilenFolgeText({
                                gewaehlt: gewaehlt.size,
                                gesamt: todos.length,
                                titel: betreff,
                                alterTitel: titel,
                            })}
                    </p>

                    <div className="vg3-modal-fuss">
                        <button type="button" className="vg3-btn" disabled={busy} onClick={onClose}>
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            className="vg3-btn primary"
                            disabled={busy || !!sperre}
                            title={sperre ?? undefined}
                            onClick={() => void teilen()}
                        >
                            {busy ? "Wird geteilt…" : "Neuen Vorgang anlegen"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VorgangTeilenDialog;
